import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  SupportNavigationAction,
  SupportNavigationWorkflow,
  SupportNavigationWorkflowResult,
} from '@web-front/support-chat';
import { baseUrlV1, consultationService } from '../../configs/api/api.config';
import {
  JOURNAL_TAB_ACTIVE_CLIENTS,
  JOURNAL_TAB_ARCHIVE_CLIENTS,
  JOURNAL_TAB_GROUP_THERAPY,
} from '../../pages/journal/journal-navigation';

type Client = { id: number; full_name: string; is_group?: boolean; is_active?: boolean };
type Consultation = {
  id: number;
  date?: string | null;
  datetime_start?: string | null;
  client_entity?: string | null;
  consultation_type?: 'online' | 'offline' | null;
};
type Collection<T> = T[] | { items?: T[]; 'hydra:member'?: T[] };
type JournalDraft = { kind: 'journal'; action: SupportNavigationAction; clients: Client[] };
type SessionClientDraft = { kind: 'session-client'; action: SupportNavigationAction; clients: Client[] };
type SessionDraft = { kind: 'sessions'; sessions: Array<{ consultation: Consultation; client?: Client }> };
type Draft = JournalDraft | SessionClientDraft | SessionDraft;

const toolPaths = {
  'emotion-detector': '/tools/emotion-detector',
  'metaphorical-cards': '/tools/metaphorical-cards',
  question: '/tools/question',
  dpdg: '/tools/dpdg',
  'life-balance-wheel': '/tools/life-balance-wheel',
  'ai-assistants': '/tools/ai-assistants',
  'color-code': '/tools/color-code',
  clouds: '/tools/clouds',
  'ai-grow': '/tools/ai-grow',
  'sand-therapy': '/tools/sand-therapy',
  arrangements: '/tools/arrangements',
} as const;

/** Browser-only navigation: IDs stay in transient local drafts. */
export function useSupportNavigationWorkflow(token: string | null): SupportNavigationWorkflow {
  const navigate = useNavigate();
  const drafts = useRef(new Map<string, Draft>());

  const request = useCallback(
    async <T>(url: string): Promise<T> => {
      const response = await fetch(url, {
        headers: { Accept: 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
      });
      if (!response.ok) throw new Error('Support navigation request failed');
      return response.json() as Promise<T>;
    },
    [token],
  );

  const openJournal = useCallback(
    (client: Client | null, section?: SupportNavigationAction['journalSection']) => {
      const journalTabId =
        section === 'group' || client?.is_group
          ? JOURNAL_TAB_GROUP_THERAPY
          : section === 'archive' || client?.is_active === false
            ? JOURNAL_TAB_ARCHIVE_CLIENTS
            : JOURNAL_TAB_ACTIVE_CLIENTS;
      navigate('/journal', {
        state: {
          ...(client ? { clientId: client.id, isClientActive: client.is_active !== false } : {}),
          journalTabId,
        },
      });
    },
    [navigate],
  );

  const openJournalSession = useCallback(
    (consultation: Consultation, client?: Client) => {
      const clientId = client?.id ?? clientIdFromEntity(consultation.client_entity);
      const journalTabId = client?.is_group
        ? JOURNAL_TAB_GROUP_THERAPY
        : client?.is_active === false
          ? JOURNAL_TAB_ARCHIVE_CLIENTS
          : JOURNAL_TAB_ACTIVE_CLIENTS;
      navigate('/journal', {
        state: {
          ...(clientId ? { clientId, isClientActive: client?.is_active !== false } : {}),
          journalTabId,
          openSessionDetail: { consultationId: consultation.id, initialTab: 'Инструменты' },
        },
      });
    },
    [navigate],
  );

  const requestClients = useCallback(
    async (name?: string): Promise<Client[]> => {
      const queryFor = (isActive: boolean) =>
        new URLSearchParams({
          ...(name ? { full_name: name } : {}),
          is_active: String(isActive),
          itemsPerPage: name ? '20' : '500',
        });
      const [active, archive] = await Promise.all([
        request<Collection<Client>>(baseUrlV1 + '/clients?' + queryFor(true)),
        request<Collection<Client>>(baseUrlV1 + '/clients?' + queryFor(false)),
      ]);
      return [...new Map([...items(active), ...items(archive)].map((client) => [client.id, client])).values()];
    },
    [request],
  );

  const requestPastSessions = useCallback(
    async (options: { clientId?: number; date?: string }): Promise<Consultation[]> => {
      const query = new URLSearchParams({
        'datetime_start[before]': new Date().toISOString(),
        'sort[datetime_start]': 'desc',
        limit: '20',
        ...(options.clientId ? { client_entity: '/api/v1/clients/' + options.clientId } : {}),
        ...(options.date ? { 'date[after]': options.date, 'date[before]': options.date } : {}),
      });
      return items(await request<Collection<Consultation>>(consultationService + '/consultations?' + query));
    },
    [request],
  );

  const newDraftId = () => globalThis.crypto?.randomUUID?.() || `navigation-${Date.now()}-${Math.random()}`;

  const sessionResult = useCallback(
    async (action: SupportNavigationAction, client?: Client): Promise<SupportNavigationWorkflowResult | null> => {
      const [sessions, allClients] = await Promise.all([
        requestPastSessions({ clientId: client?.id, date: action.sessionQuery === 'date' ? action.date : undefined }),
        client ? Promise.resolve([client]) : requestClients(),
      ]);
      if (sessions.length === 0) {
        return {
          kind: 'info',
          message:
            action.sessionQuery === 'date'
              ? 'За указанную дату прошлых сессий не найдено.'
              : 'Прошлых сессий не найдено.',
        };
      }
      const clientsById = new Map(allClients.map((item) => [item.id, item]));
      const results = sessions.map((consultation) => {
        const clientId = clientIdFromEntity(consultation.client_entity);
        return { consultation, client: clientId === undefined ? undefined : clientsById.get(clientId) };
      });
      if (action.presentation === 'open' && (action.sessionQuery === 'latest' || results.length === 1)) {
        openJournalSession(results[0].consultation, results[0].client);
        return null;
      }
      const draftId = newDraftId();
      drafts.current.set(draftId, { kind: 'sessions', sessions: results });
      return {
        kind: 'list',
        draftId,
        title: action.sessionQuery === 'history' ? 'Прошлые сессии' : 'Найденные сессии',
        items: results.map(({ consultation, client }) => ({
          id: `session:${consultation.id}`,
          title: formatSessionTime(consultation.datetime_start) || consultation.date || 'Сессия',
          description: `${client?.full_name || 'Клиент'} · ${consultation.consultation_type === 'offline' ? 'офлайн-сессия' : 'видеоконсультация'}`,
          entityKind: client?.is_group ? 'group' : 'consultation',
          actionLabel: 'Открыть журнал',
        })),
      };
    },
    [openJournalSession, requestClients, requestPastSessions],
  );

  const openDestination = useCallback(
    (action: SupportNavigationAction): SupportNavigationWorkflowResult | null => {
      if (action.destination === 'profile') navigate('/profile');
      else if (action.destination === 'consultations') navigate('/consultations');
      else if (action.destination === 'calendar') navigate('/consultations', { state: { openCalendar: true } });
      else if (action.destination === 'tariffs') navigate('/tariffs');
      else if (action.destination === 'tools') navigate('/tools');
      else if (action.destination === 'tool' && action.tool) navigate(toolPaths[action.tool]);
      else if (action.destination === 'info') navigate('/info');
      else if (action.destination === 'referral_management') navigate('/referal-management');
      else if (action.destination === 'support_tickets') navigate('/support-tickets');
      else if (action.destination === 'journal') openJournal(null, action.journalSection);
      else return { kind: 'info', message: 'Не удалось определить нужный раздел.' };
      return null;
    },
    [navigate, openJournal],
  );

  const navigationWorkflow = useCallback(
    async (action: SupportNavigationAction): Promise<SupportNavigationWorkflowResult | null> => {
      if (action.destination === 'journal_session') {
        try {
          if (!action.clientName) return await sessionResult(action);
          const clients = await requestClients(action.clientName);
          if (clients.length === 0)
            return {
              kind: 'info',
              message: `Не нашёл клиента «${action.clientName}». Проверьте имя и повторите запрос.`,
            };
          const exact = clients.filter(
            (client) => normalizeName(client.full_name) === normalizeName(action.clientName || ''),
          );
          const matches = exact.length === 1 ? exact : clients;
          if (matches.length === 1) return await sessionResult(action, matches[0]);
          const draftId = newDraftId();
          drafts.current.set(draftId, { kind: 'session-client', action, clients: matches });
          return {
            kind: 'selection',
            draftId,
            message: 'Нашёл несколько клиентов с таким именем. Выберите нужного:',
            choices: matches.map((client) => ({
              id: `client:${client.id}`,
              label: client.full_name,
              entityKind: client.is_group ? 'group' : 'client',
            })),
          };
        } catch {
          return { kind: 'info', message: 'Не удалось получить прошлые сессии. Попробуйте ещё раз.' };
        }
      }
      if (action.destination !== 'journal' || !action.clientName) return openDestination(action);
      try {
        const clients = await requestClients(action.clientName);
        if (clients.length === 0)
          return {
            kind: 'info',
            message: `Не нашёл клиента «${action.clientName}». Проверьте имя или откройте журнал без выбора клиента.`,
          };
        const exact = clients.filter(
          (client) => normalizeName(client.full_name) === normalizeName(action.clientName || ''),
        );
        const matches = exact.length === 1 ? exact : clients;
        if (matches.length === 1) {
          openJournal(matches[0], action.journalSection);
          return null;
        }
        const draftId = newDraftId();
        drafts.current.set(draftId, { kind: 'journal', action, clients: matches });
        return {
          kind: 'selection',
          draftId,
          message: 'Нашёл несколько клиентов с таким именем. Выберите нужного:',
          choices: matches.map((client) => ({
            id: `client:${client.id}`,
            label: client.full_name,
            entityKind: client.is_group ? 'group' : 'client',
          })),
        };
      } catch {
        return { kind: 'info', message: 'Не удалось найти клиента. Попробуйте ещё раз или откройте журнал вручную.' };
      }
    },
    [openDestination, openJournal, requestClients, sessionResult],
  );

  const select = useCallback(
    async (draftId: string, choiceId: string): Promise<SupportNavigationWorkflowResult | null> => {
      const draft = drafts.current.get(draftId);
      if (!draft) return { kind: 'info', message: 'Выбор устарел. Повторите запрос.' };
      if (choiceId.startsWith('session:') && draft.kind === 'sessions') {
        const sessionId = Number(choiceId.slice('session:'.length));
        const session = draft.sessions.find((item) => item.consultation.id === sessionId);
        drafts.current.delete(draftId);
        if (!session) return { kind: 'info', message: 'Запись больше недоступна. Повторите запрос.' };
        openJournalSession(session.consultation, session.client);
        return null;
      }
      if (!choiceId.startsWith('client:') || draft.kind === 'sessions')
        return { kind: 'info', message: 'Не удалось обработать выбранный вариант.' };
      const clientId = Number(choiceId.slice('client:'.length));
      const client = draft.clients.find((item) => item.id === clientId);
      drafts.current.delete(draftId);
      if (!client) return { kind: 'info', message: 'Выбранный клиент больше недоступен. Повторите запрос.' };
      if (draft.kind === 'journal') {
        openJournal(client, draft.action.journalSection);
        return null;
      }
      try {
        return await sessionResult(draft.action, client);
      } catch {
        return { kind: 'info', message: 'Не удалось получить прошлые сессии. Попробуйте ещё раз.' };
      }
    },
    [openJournal, openJournalSession, sessionResult],
  );

  const dismiss = useCallback((draftId?: string) => {
    if (draftId) drafts.current.delete(draftId);
  }, []);

  return { navigate: navigationWorkflow, select, dismiss };
}

function items<T>(value: Collection<T>): T[] {
  return Array.isArray(value) ? value : value.items || value['hydra:member'] || [];
}

function normalizeName(value: string) {
  return value.trim().toLocaleLowerCase('ru-RU');
}

function clientIdFromEntity(value?: string | null): number | undefined {
  const match = value?.match(/\/clients\/(\d+)$/);
  return match ? Number(match[1]) : undefined;
}

function formatSessionTime(value?: string | null) {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value.slice(0, 16)
    : new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}
